import { Component, ChangeDetectionStrategy, OnInit, OnDestroy, inject } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { KeycloakService } from 'keycloak-angular';
import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { AccountsContextService } from './accounts/account-context.service';
import { AccountsService } from './accounts/accounts.service';
import { AclService } from './acl/acl.service';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'body[root]',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit, OnDestroy {
  public readonly splashscreenVisible: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public readonly accountsService: AccountsService;
  public readonly aclService: AclService;

  private readonly router: Router;
  private readonly ctxService: AccountsContextService;
  private readonly destroyed$: Subject<void> = new Subject();

  constructor() {
    this.accountsService = inject(AccountsService);
    this.aclService = inject(AclService);
    this.router = inject(Router);
    this.ctxService = inject(AccountsContextService);
    const keycloakService = inject(KeycloakService);

    const isLoggedIn = keycloakService.isLoggedIn();
    if (isLoggedIn) {
      /**
       * Init core services only when user is logged in
       * to avoid unneeded requests to BE
       */
      this.ctxService.initialize();
      this.accountsService.init();
    }
  }

  public mergeRouteParams() {
    let params = {};
    const stack: ActivatedRouteSnapshot[] = [this.router.routerState.snapshot.root];
    while (stack.length > 0) {
      const route = stack.pop();
      params = { ...params, ...route?.params };
      if (route?.children) {
        stack.push(...route.children);
      }
    }

    return params;
  }

  public ngOnInit() {
    combineLatest([
      this.accountsService.areAccountsLoaded$,
      this.aclService.isAclLoaded$,
    ])
      .pipe(
        filter(([areAccountsLoaded, isAclLoaded]) => areAccountsLoaded && isAclLoaded),
      )
      .subscribe(() => {
        /*
        Thanks to this there is no blink effect of loading screen but i don't know why
        ToDo: investigate why it's not blinking */
        this.splashscreenVisible.next(false);
      });

    this.router.events
      .pipe(
        takeUntil(this.destroyed$)
      )
      .subscribe(e => {
        if (e instanceof NavigationEnd) {
          this.ctxService.updateContextIdIfNeeded(this.mergeRouteParams());
        }
      });
  }

  public ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

}
